import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'src/common';
import styles from './index.module.scss';
import classNames from 'classnames';
import { ProcurementsDisplay } from 'src/models/procurements/types';
import {
  useBidIdsFromSelectedTenders,
  useOriginsIdsFromSelectedTenders,
  useReadUnreadIdsFromSelectedTenders
} from 'src/models/procurements/Tenders/hooks';
import { WorkspacePicker, RejectTendersButton } from 'src/shared';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faEnvelopeOpen } from '@fortawesome/pro-light-svg-icons';
import useOverviewMode from 'src/reactiveVars/overviewModeVar';
import { BidItemType } from '@tendium/prom-types/tender';
import { IProcToRejecting } from 'src/shared/Tender/RejectTendersButton';
import useSelectedTenders from 'src/reactiveVars/SelectedTendersVar';
import { useCloseSidebar } from 'src/shared/InfoSidebar/hooks';
import { useUpdateIsReadTenders } from 'src/models/procurements/hooks';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';

export const OverviewActionsMultiple: FC = () => {
  const { t } = useTranslation();
  const procurementTabInfo = useOverviewMode();
  const [selectedTenders, setSelectedTenders] = useSelectedTenders();
  const originIds = useOriginsIdsFromSelectedTenders();
  const bidIds = useBidIdsFromSelectedTenders();
  const onCloseSidebar = useCloseSidebar();

  const clearSelectedTenders = useCallback(() => {
    onCloseSidebar();
    setSelectedTenders([]);
  }, [onCloseSidebar, setSelectedTenders]);
  const tendersToRejecting: IProcToRejecting[] = useMemo(
    () =>
      selectedTenders.map(tender => {
        const { id, isRejected } = tender;
        return { id, isRejected };
      }),
    [selectedTenders]
  );
  const { selectedIsReadIds, selectedIsUnReadIds } = useReadUnreadIdsFromSelectedTenders();

  const isSelectedSomeUnread = selectedTenders.some(r => !r.isRead);
  const [updateTendersAsRead] = useUpdateIsReadTenders();

  const currentWS = useMemo(() => {
    return procurementTabInfo?.tab === ProcurementsDisplay.WORKSPACE && procurementTabInfo.workspaceId;
  }, [procurementTabInfo]);

  const isMatchedTab = useMemo(() => {
    return procurementTabInfo?.tab === ProcurementsDisplay.MATCHED;
  }, [procurementTabInfo]);

  const [{ vars: args }] = useApiTenders();
  const isHandledTab = useMemo(() => isMatchedTab && args.isUnhandled === false, [args.isUnhandled, isMatchedTab]);

  const changeReadStatusSelectedProcurements = useCallback(() => {
    const tenderIds = isSelectedSomeUnread ? selectedIsUnReadIds : selectedIsReadIds;
    updateTendersAsRead({
      tenderIds,
      isRead: isSelectedSomeUnread,
      onFinish: clearSelectedTenders
    });
  }, [clearSelectedTenders, isSelectedSomeUnread, selectedIsReadIds, selectedIsUnReadIds, updateTendersAsRead]);

  return (
    <ul
      className={classNames(styles.actionsMultipleTenders, {
        [styles.isAllActions]: (!isHandledTab || !currentWS) && isMatchedTab,
        [styles.isWsActions]: currentWS
      })}
    >
      {(!isHandledTab || !currentWS) && (
        <li className={classNames(styles.actionMultipleTenders, styles.addToWsAction)}>
          <WorkspacePicker
            bidIds={bidIds}
            originIds={originIds}
            originType={BidItemType.Procurement}
            eventSource={'OverviewMultipleTenders'}
            onChange={clearSelectedTenders}
            isSelected={!!selectedTenders.length}
          />
        </li>
      )}
      {isMatchedTab && (
        <li className={classNames(styles.actionMultipleTenders, styles.rejectAction)}>
          <RejectTendersButton
            procurements={tendersToRejecting}
            onFinish={clearSelectedTenders}
            eventSource={'OverviewActionsMultiple'}
          />
        </li>
      )}
      <li className={classNames(styles.actionMultipleTenders, styles.unreadAction)}>
        <Tooltip
          title={t(isSelectedSomeUnread ? 'Tenders.markAllProcurementsAsRead' : 'Tenders.markAllProcurementsAsUnread')}
        >
          <FontAwesomeIcon
            icon={isSelectedSomeUnread ? faEnvelopeOpen : faEnvelope}
            onClick={changeReadStatusSelectedProcurements}
            className={classNames(styles.asReadButton)}
          />
        </Tooltip>
      </li>
    </ul>
  );
};
export default OverviewActionsMultiple;
